/**
 * CivilX TMS telemetry — fire-and-forget event ingest with batching.
 *
 * Usage:
 *   import { track, trackPageView, initTelemetry } from '../lib/telemetry';
 *   track('click', 'cta-create-load', { loadType: 'ftl' });
 *
 * Design notes:
 *   - Buffers events in-memory; flushes every 8s OR on `pagehide` (mobile safe).
 *   - Uses navigator.sendBeacon on unload for guaranteed delivery on mobile.
 *   - Session id is sessionStorage-backed (survives reloads, dies on tab close).
 *   - Never throws — telemetry must never break the app.
 */

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const ENDPOINT = `${API}/telemetry/events`;
const FLUSH_INTERVAL_MS = 8000;
const MAX_BATCH = 50;
const SESSION_KEY = 'civilx_tms_telemetry_session';

let buffer = [];
let flushTimer = null;
let initialized = false;

const getSessionId = () => {
  try {
    let sid = sessionStorage.getItem(SESSION_KEY);
    if (!sid) {
      sid = `s_${Date.now()}_${Math.random().toString(36).slice(2, 10)}`;
      sessionStorage.setItem(SESSION_KEY, sid);
    }
    return sid;
  } catch { return null; }
};

const enqueue = (event_type, target, meta) => {
  try {
    if (typeof window === 'undefined') return;
    buffer.push({
      event_type,
      target: target || null,
      page: window.location?.pathname + (window.location?.search || ''),
      meta: meta && typeof meta === 'object' ? meta : null,
      ts: new Date().toISOString(),
      session_id: getSessionId(),
    });
    if (buffer.length >= MAX_BATCH) flush();
    else scheduleFlush();
  } catch { /* swallow */ }
};

const scheduleFlush = () => {
  if (flushTimer) return;
  flushTimer = setTimeout(() => { flushTimer = null; flush(); }, FLUSH_INTERVAL_MS);
};

const flush = (useBeacon = false) => {
  if (!buffer.length) return;
  const events = buffer.splice(0, buffer.length);
  const body = JSON.stringify({ events });
  try {
    if (useBeacon && navigator.sendBeacon) {
      navigator.sendBeacon(ENDPOINT, new Blob([body], { type: 'application/json' }));
      return;
    }
    fetch(ENDPOINT, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body,
      keepalive: true,
    }).catch(() => { /* swallow */ });
  } catch { /* swallow */ }
};

export const track = (eventType, target, meta) => enqueue(eventType, target, meta);

export const trackPageView = (path) => {
  // path is optional; enqueue() reads window.location anyway. Kept for explicit calls.
  enqueue('page_view', null, path ? { explicit: path } : null);
};

export const initTelemetry = () => {
  if (initialized || typeof window === 'undefined') return;
  initialized = true;
  // Flush on tab/page hide — covers mobile + back/forward cache.
  window.addEventListener('pagehide', () => flush(true), { capture: true });
  window.addEventListener('beforeunload', () => flush(true));
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'hidden') flush(true);
  });
};
